import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { apiEndpoints, apiPublic } from '../../utils/apiService';

type PublicStore = {
  id: number;
  name: string;
  logo_url?: string | null;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
};

export default function OptInSuccess() {
  const { storeUuid } = useParams();
  const location = useLocation();

  const [store, setStore] = useState<PublicStore | null>(null);

  useEffect(() => {
    async function loadStore() {
      if (!storeUuid) return;
      try {
        const res = await apiPublic.get(apiEndpoints.getPublicStore(storeUuid));
        if (res && res.ok) {
          const data = await res.json();
          setStore({
            id: data.id,
            name: data.name,
            logo_url: data.logo_url || null,
            address_line_1: data.address_line_1 || '',
            address_line_2: data.address_line_2 || '',
            city: data.city || '',
            state: data.state || '',
            postal_code: data.postal_code || '',
            country: data.country || '',
          });
        }
      } catch {
        // The saved-permission result remains valid if branding cannot load.
      }
    }
    loadStore();
  }, [storeUuid]);

  const askArthurLogo =
    (process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}` : '') +
    '/images/logo/logo.svg';
  const logoSrc = store?.logo_url || askArthurLogo;
  const usingFallbackLogo = !store?.logo_url;

  if (!location.state?.permissionRecorded)
    return (
      <main className="p-8">
        This page does not confirm SMS permission.{' '}
        <Link className="underline" to={`/opt-in/${storeUuid}`}>
          Review the messaging form
        </Link>
        .
      </main>
    );
  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      <header className="px-4 py-4">
        <div className="max-w-6xl mx-auto">
          <a
            href="https://www.askarthur.ai"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="AskArthur website"
          >
            <img
              src={askArthurLogo}
              alt="AskArthur"
              className="h-8 md:h-10 w-auto"
              onError={(e) => {
                const img = e.currentTarget as HTMLImageElement;
                if (!img.dataset.fallbackTried) {
                  img.dataset.fallbackTried = '1';
                  img.src = '/images/logo/logo.svg';
                } else {
                  img.onerror = null;
                  img.src = '/images/logo/logo.svg';
                }
              }}
            />
          </a>
        </div>
      </header>
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl shadow border border-gray-200 p-8 md:p-12 text-center">
            <div className="flex justify-center mb-6">
              <img
                src={logoSrc}
                alt={usingFallbackLogo ? 'AskArthur' : 'Store logo'}
                className={
                  usingFallbackLogo
                    ? 'h-7 md:h-8 w-auto object-contain'
                    : 'h-16 w-16 rounded-full object-contain'
                }
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).onerror = null;
                  (e.currentTarget as HTMLImageElement).src = askArthurLogo;
                }}
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-semibold mb-2">
              You're all set!
            </h1>
            <p className="text-gray-600">
              Thanks for opting in to SMS updates. You’ll receive order
              confirmations, ready‑for‑pickup alerts, and support messages
              powered by AskArthur.
            </p>
          </div>
        </div>
      </main>
      <footer className="py-10 text-center text-gray-500 text-sm">
        <div>Ronin Tech LLC · AskArthur. All rights reserved.</div>
        <div>16192 Coastal Highway</div>
        <div>Lewes, DE 19958, United States</div>
      </footer>
    </div>
  );
}
